import React, { memo, useContext } from 'react';
import styled from 'styled-components';
import { AppContext } from 'src/components/app-context';
import { layout } from 'src/components/base';

import { Clickable as ClickableComponent } from 'src/components/clickable';
import ExpertAvatar from '../expert-avatar';

import type { AdvizedExpert, OtherExpert } from '../../../../types';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

const { spacing025, spacing100 } = layout.measurements;

type ListProps = {
  size: number;
};

const List = styled.div<ListProps>`
  display: grid;
  grid-template-columns: ${p => `repeat(auto-fit, ${p.size}px)`};
  column-gap: ${spacing100};
  justify-content: start;
  align-items: start;
  width: 100%;
`;

const Name = styled.div`
  font-weight: 600;
  margin-top: ${spacing025};
  color: ${p => p.theme.text.primary};
`;

const Expert = styled.div`
  max-width: max-content;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Clickable = styled(ClickableComponent)`
  &:hover {
    ${Name} {
      text-decoration: underline;
    }
  }
`;

type RestProps = {
  size: number;
};

const Rest = styled.div<RestProps>`
  width: ${p => p.size}px;
  height: ${p => p.size}px;
  background: ${p => p.theme.icons.quaternary};
  border-radius: ${p => p.size}px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
`;

type Props = {
  avatarSize?: number;
  className?: string;
  count: number;
  experts: Array<AdvizedExpert | OtherExpert | null>;
  limit?: number;
  linkRest?: boolean;
};

const ListOfExperts = ({
  avatarSize = 64,
  className,
  count,
  experts,
  limit = 4,
  linkRest = false,
}: Props) => {
  const { renderMode, linkTo } = useContext(AppContext);
  const restCount = count - limit;

  const renderRest = () => (
    <Expert>
      <Rest size={avatarSize}>+{restCount}</Rest>
    </Expert>
  );

  return (
    <List size={avatarSize} className={className} data-test="ListOfExperts">
      {experts.slice(0, limit).map((expert, index) => {
        if (!expert) return null;

        const children = (
          <Expert key={expert.name}>
            <ExpertAvatar
              size={avatarSize}
              displayName={expert.name}
              avatar={expert.avatar}
            />
            <Name>{expert.name.split(' ')[0]}</Name>
          </Expert>
        );

        return (expert as AdvizedExpert).url ? (
          <Clickable
            key={expert.name}
            onClick={() =>
              trackInternalNavigationClick({
                scope: InternalNavigationScope.EXPERT_LIST_PROFILE_GO_TO,
                nextPage: (expert as AdvizedExpert).url,
                index,
              })
            }
            isExternal={renderMode === 'native'}
            to={linkTo(
              'expertProfile',
              { semantic_id: (expert as AdvizedExpert).url },
              renderMode === 'native' ? { view: 'native' } : undefined
            )}
          >
            {children}
          </Clickable>
        ) : (
          children
        );
      })}
      {restCount > 0 &&
        (linkRest ? (
          <Clickable
            isExternal={renderMode === 'native'}
            to={linkTo('experts')}
            onClick={() =>
              trackInternalNavigationClick({
                scope: InternalNavigationScope.EXPERT_LIST_EXPERT_PAGE_GO_TO,
                nextPage: linkTo('experts'),
                index: limit,
              })
            }
          >
            {renderRest()}
          </Clickable>
        ) : (
          renderRest()
        ))}
    </List>
  );
};

export default memo(ListOfExperts);
